import { useTranslation } from 'react-i18next';
import {
  CompaniesLabel,
  Container,
  FirstRow,
  NoContentLabel,
  AddWrapper,
  AddLabel,
  RowWrapper,
  InputWrapper,
  LeftSide,
  RightSide,
  ToggleGroupWrapper,
  ToggleLabel,
  NoContentLabelWrapper,
} from './BuyerModels.styled';
import Input from 'components/Input/Input';
import { useEffect, useState } from 'react';
import { useDebounce } from 'utils/hooks/useDebounce';
import { BUYER_MODELS_PER_PAGE } from './constants';
import {
  useDeleteBuyerModel,
  useGetBuyerModels,
  useToggleOrderAllExternallyModels,
  useToggleOrderAllInternallyModels,
} from './hooks';
import { queryClient } from 'index';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { Plus, MagnifyingGlass as Search } from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { AddModelToBuyerModal } from 'components/Modals/AddModelToBuyerModal/AddModelToBuyerModal';
import { COLORS } from 'assets/styled';
import BuyerModelsRowSkeleton from './BuyerModelsRow/BuyerModelsRowSkeleton/BuyerModelsRowSkeleton';
import { IBuyerModel } from 'types/BuyerModel';
import BuyerModelsRow from './BuyerModelsRow/BuyerModelsRow';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import Pagination from 'components/Pagination/Pagination';

interface IBuyerModelsProps {
  sellerId: number;
  buyerId: number;
}

const BuyerModels = ({ sellerId, buyerId }: IBuyerModelsProps) => {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [page, setPage] = useState<number>(1);
  const [isAddModelModalOpen, setIsAddModelModalOpen] =
    useState<boolean>(false);
  const [isDeleteBuyerModelModalOpen, setIsDeleteBuyerModelModalOpen] =
    useState<boolean>(false);
  const [buyerModelIdForDeletion, setBuyerModelIdForDeletion] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  const { data: buyerModelsData, isLoading: isLoadingBuyerModels } =
    useGetBuyerModels(
      page,
      BUYER_MODELS_PER_PAGE,
      debouncedSearchBy,
      buyerId,
      sellerId
    );

  const buyerModels: IBuyerModel[] = buyerModelsData?.data || [];

  const showPagination =
    buyerModelsData?.pagination?.total &&
    buyerModelsData?.pagination?.total > BUYER_MODELS_PER_PAGE;

  const {
    mutate: toggleOrderAllInternallyModels,
    isLoading: isLoadingToggleOrderAllInternallyModels,
    isSuccess: isSuccessToggleOrderAllInternallyModels,
    reset: resetToggleOrderAllInternallyModels,
  } = useToggleOrderAllInternallyModels();

  const {
    mutate: toggleOrderAllExternallyModels,
    isLoading: isLoadingToggleOrderAllExternallyModels,
    isSuccess: isSuccessToggleOrderAllExternallyModels,
    reset: resetToggleOrderAllExternallyModels,
  } = useToggleOrderAllExternallyModels();

  useEffect(() => {
    if (
      isSuccessToggleOrderAllInternallyModels ||
      isSuccessToggleOrderAllExternallyModels
    ) {
      queryClient.invalidateQueries([ReactQueryKeys.GET_BUYER_MODELS]);
      resetToggleOrderAllInternallyModels();
      resetToggleOrderAllExternallyModels();
    }
  }, [
    isSuccessToggleOrderAllInternallyModels,
    isSuccessToggleOrderAllExternallyModels,
  ]);

  const isLoading =
    isLoadingBuyerModels ||
    isLoadingToggleOrderAllInternallyModels ||
    isLoadingToggleOrderAllExternallyModels;

  const { mutate: deleteBuyerModel, isLoading: isLoadingDeleteBuyerModel } =
    useDeleteBuyerModel();

  return (
    <Container>
      <FirstRow>
        <RowWrapper>
          <LeftSide>
            <CompaniesLabel>{t('Models')}</CompaniesLabel>
            <InputWrapper>
              <Input
                placeholder={t('Search')}
                width={'100%'}
                wrapperStyles={{ width: '100%' }}
                onChange={(e: any) => setSearchBy(e.target.value)}
                icon={Search}
              />
            </InputWrapper>
          </LeftSide>
          <RightSide>
            <ToggleGroupWrapper>
              <ToggleLabel>{t('Toggle all Internal Ordering')}</ToggleLabel>
              <ToggleSwitch
                disabled={isLoading}
                isChecked={
                  buyerModelsData?.ordering_summary?.can_order_all_internally ??
                  'native'
                }
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (
                    buyerModelsData?.ordering_summary?.can_order_all_internally
                  ) {
                    toggleOrderAllInternallyModels({
                      buyerId,
                      companyId: sellerId,
                      canOrderInternally: false,
                    });
                  } else {
                    toggleOrderAllInternallyModels({
                      buyerId,
                      companyId: sellerId,
                      canOrderInternally: true,
                    });
                  }
                }}
                size={'large'}
              />
            </ToggleGroupWrapper>

            <ToggleGroupWrapper>
              <ToggleLabel>{t('Toggle all External Ordering')}</ToggleLabel>
              <ToggleSwitch
                disabled={isLoading}
                isChecked={
                  buyerModelsData?.ordering_summary?.can_order_all_externally ??
                  'native'
                }
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (
                    buyerModelsData?.ordering_summary?.can_order_all_externally
                  ) {
                    toggleOrderAllExternallyModels({
                      buyerId,
                      companyId: sellerId,
                      canOrderExternally: false,
                    });
                  } else {
                    toggleOrderAllExternallyModels({
                      buyerId,
                      companyId: sellerId,
                      canOrderExternally: true,
                    });
                  }
                }}
                size={'large'}
              />
            </ToggleGroupWrapper>

            <AddWrapper onClick={() => setIsAddModelModalOpen(true)}>
              <Icon svg={Plus} color={COLORS.GREEN} size={20} />
              <AddLabel>{t('Add Model')}</AddLabel>
            </AddWrapper>
          </RightSide>
        </RowWrapper>
      </FirstRow>
      {buyerModels.length ? (
        buyerModels.map((buyerModel: IBuyerModel, index: number) => (
          <BuyerModelsRow
            key={`buyer-models-row-${index}`}
            buyerModel={buyerModel}
            withoutLine={buyerModels.length - 1 === index}
            isLoadingParent={isLoading}
            // onDelete={(buyerModelId: number) => {
            //   setIsDeleteBuyerModelModalOpen(true);
            //   setBuyerModelIdForDeletion(buyerModelId);
            // }}
          />
        ))
      ) : isLoadingBuyerModels ? (
        Array.from({ length: BUYER_MODELS_PER_PAGE }, (_, index) => (
          <BuyerModelsRowSkeleton
            key={index}
            index={index}
            withoutLine={BUYER_MODELS_PER_PAGE - 1 === index}
          />
        ))
      ) : (
        <NoContentLabelWrapper>
          <NoContentLabel>{t('No models found for this buyer')}</NoContentLabel>
        </NoContentLabelWrapper>
      )}
      {showPagination ? (
        <Pagination
          page={page}
          perPage={BUYER_MODELS_PER_PAGE}
          total={buyerModelsData?.pagination?.total || '0'}
          setPage={setPage}
        />
      ) : null}
      <AddModelToBuyerModal
        isOpen={isAddModelModalOpen}
        setIsOpen={setIsAddModelModalOpen}
        buyerId={buyerId}
        companyId={sellerId}
      />
      <DangerModal
        isOpen={isDeleteBuyerModelModalOpen}
        setIsOpen={setIsDeleteBuyerModelModalOpen}
        title={t('Confirm removal')}
        submit={{
          onClick: () => {
            deleteBuyerModel(buyerModelIdForDeletion!);
            setIsDeleteBuyerModelModalOpen(false);
            setBuyerModelIdForDeletion(null);
          },
          text: t('Remove'),
          disabled: isLoadingDeleteBuyerModel,
        }}
        cancel={{
          onClick: () => {
            setIsDeleteBuyerModelModalOpen(false);
            setBuyerModelIdForDeletion(null);
          },
        }}
        description={t(
          'Are you sure you want to remove access of this Model for this Buyer?'
        )}
      />
    </Container>
  );
};

export default BuyerModels;
